// Bulk-entry modal for the KPI Overview header's "Log period (bulk)"
// button. One row per non-archived KPI, seeded at open time (Pattern 0
// seed-on-open — the modal is mounted eagerly before the KPI queries
// resolve, so mount-time seeding would render an empty body); pre-fills
// period via `nextUnloggedPeriod`; auto-RAGs on value input via the
// shared `computeAutoRag` helper. On Save, packs populated rows into a
// `POST /kpi-period-batch` body via `useBulkCreateKpiPeriods` and
// renders a partial-success banner + per-row error text. Rows that
// succeed are remembered locally and skipped on retry so the second
// submit targets only the failed rows.
//
// Empty rows (no value AND no RAG) are silently skipped — the same
// period-row carpet bomb pattern a contract manager uses when a KPI
// doesn't report this cycle. Incomplete rows (RAG without value) block
// the whole submit with an inline error.

import {
  useCallback,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { CheckCircle } from "lucide-react";
import Modal from "../../../../common/Modal";
import InfoHint from "../../../../common/InfoHint";
import Button from "../../../../common/Button";
import Alert from "../../../../common/Alert";
import EvidenceLinksField from "../../shared/EvidenceLinksField";
import { nextUnloggedPeriod } from "../../../../../lib/kpi/period";
import {
  computeAutoRag,
  parseTarget,
} from "../../../../../lib/kpi/autoRag";
import { useBulkCreateKpiPeriods } from "../../../../../hooks/useTracking";
import {
  KPI_BOOLEAN_MET,
  KPI_BOOLEAN_NOT_MET,
  type EvidenceLink,
  type Kpi,
  type KpiBatchItem,
  type KpiMeasurement,
  type KpiReport,
  type KpiValueType,
  type RagRating,
} from "../../../../../types";

export interface BulkPeriodEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
  kpis: Kpi[];
  measurements: KpiMeasurement[];
  reports: KpiReport[];
  now?: Date;
}

interface RowState {
  period: string;
  value: string;
  manualRag: RagRating | null;
  commentary: string;
  evidenceLinks: EvidenceLink[];
  commentaryOpen: boolean;
  evidenceOpen: boolean;
  error?: string;
}

const RAG_OPTIONS: RagRating[] = ["red", "amber", "green"];
const BOOLEAN_AMBER_TOOLTIP = "Met/Not-met KPIs use Green or Red only";

function emptyRow(period: string): RowState {
  return {
    period,
    value: "",
    manualRag: null,
    commentary: "",
    evidenceLinks: [],
    commentaryOpen: false,
    evidenceOpen: false,
  };
}

function rowIsEmpty(row: RowState): boolean {
  return row.value.trim() === "" && row.manualRag === null;
}

function rowIsIncomplete(row: RowState): boolean {
  // PO rule: RAG picked without a value (or vice versa) is a partial
  // row and blocks submit — either both or neither. A value alone is
  // fine because the auto-RAG fills the RAG side.
  const hasValue = row.value.trim() !== "";
  const hasRag = row.manualRag !== null;
  return hasRag && !hasValue;
}

export default function BulkPeriodEntryModal({
  isOpen,
  onClose,
  contractId,
  kpis,
  measurements,
  reports,
  now,
}: BulkPeriodEntryModalProps) {
  const activeKpis = useMemo(
    () => kpis.filter((k) => !k.archivedAt),
    [kpis],
  );

  const [rows, setRows] = useState<Record<string, RowState>>({});

  // Track which KPI ids already succeeded so the retry submit skips
  // them — otherwise we'd write a duplicate period on the server and
  // get a unique-period rejection.
  const [succeededIds, setSucceededIds] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<string | null>(null);

  // Pattern 0: seed row state during render each time the modal opens.
  // KpiTab mounts this modal eagerly while the KPI/measurement queries
  // are still pending, so a mount-time initialiser would seed from an
  // empty list and leave the body blank once the data arrives. Seeding
  // keys off the open flag alone — a React Query refetch while the
  // modal is open must not wipe what the user has typed. Pre-filled
  // period uses nextUnloggedPeriod; measurement / report history is
  // passed all-at-once so each row's filter runs over the parent's own
  // children.
  const [seededOpen, setSeededOpen] = useState(false);
  if (isOpen !== seededOpen) {
    setSeededOpen(isOpen);
    if (isOpen) {
      const out: Record<string, RowState> = {};
      for (const kpi of activeKpis) {
        const period = nextUnloggedPeriod(kpi, measurements, reports, now);
        out[kpi.id] = emptyRow(period);
      }
      setRows(out);
      setSucceededIds(new Set());
      setBanner(null);
    }
  }

  const mutation = useBulkCreateKpiPeriods();

  const updateRow = useCallback(
    (id: string, patch: Partial<RowState>) => {
      setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (activeKpis.length === 0) return;

      // Reset per-row errors from the previous attempt.
      setRows((prev) => {
        const next: typeof prev = {};
        for (const [id, r] of Object.entries(prev))
          next[id] = { ...r, error: undefined };
        return next;
      });

      // Block the whole submit on any incomplete row so the user sees
      // the partial-row hint for every offending row at once.
      const incompleteIds: string[] = [];
      for (const kpi of activeKpis) {
        if (succeededIds.has(kpi.id)) continue;
        const row = rows[kpi.id];
        if (row && rowIsIncomplete(row)) incompleteIds.push(kpi.id);
      }
      if (incompleteIds.length > 0) {
        setRows((prev) => {
          const next: typeof prev = {};
          for (const [id, r] of Object.entries(prev)) {
            next[id] = incompleteIds.includes(id)
              ? {
                  ...r,
                  error: "Complete both fields or leave both blank",
                }
              : r;
          }
          return next;
        });
        return;
      }

      // Block rows whose period already has a logged entry on THIS KPI —
      // defence-in-depth alongside the server's per-item reject. Prevents
      // a silently-duplicated write when storage already has a matching
      // (parentId, period) row. Empty rows are skipped; succeeded rows
      // are already excluded from the retry.
      const duplicateIds: string[] = [];
      for (const kpi of activeKpis) {
        if (succeededIds.has(kpi.id)) continue;
        const row = rows[kpi.id];
        if (!row || rowIsEmpty(row)) continue;
        const p = row.period.trim();
        if (!p) continue;
        const hasDupe =
          measurements.some((m) => m.parentId === kpi.id && m.period === p) ||
          reports.some((r) => r.parentId === kpi.id && r.period === p);
        if (hasDupe) duplicateIds.push(kpi.id);
      }
      if (duplicateIds.length > 0) {
        setRows((prev) => {
          const next: typeof prev = { ...prev };
          for (const id of duplicateIds) {
            next[id] = {
              ...next[id],
              error:
                "This period already has a logged entry. Use the pencil icon on the history row to amend it instead.",
            };
          }
          return next;
        });
        return;
      }

      // Build the batch body from non-empty, non-succeeded rows.
      const items: KpiBatchItem[] = [];
      for (const kpi of activeKpis) {
        if (succeededIds.has(kpi.id)) continue;
        const row = rows[kpi.id];
        if (!row || rowIsEmpty(row)) continue;

        const autoRag = computeAutoRag(row.value, kpi);
        const effectiveRag: RagRating | null = row.manualRag ?? autoRag;
        if (!effectiveRag) continue; // defensive: value with no resolvable RAG

        items.push({
          kpiId: kpi.id,
          measurement: {
            period: row.period,
            value: row.value.trim(),
            date: new Date().toISOString().slice(0, 10),
          },
          report: {
            period: row.period,
            ragRating: effectiveRag,
            ...(row.commentary.trim() !== ""
              ? { commentary: row.commentary.trim() }
              : {}),
            ...(row.evidenceLinks.length > 0
              ? { evidenceLinks: row.evidenceLinks }
              : {}),
          },
        });
      }

      if (items.length === 0) return;

      const response = await mutation.mutateAsync({
        contractId,
        items,
      });

      const okCount = response.items.filter((r) => r.ok).length;
      const failCount = response.items.length - okCount;

      // Mark succeeded rows so the retry submit leaves them alone.
      const nextSucceeded = new Set(succeededIds);
      for (const r of response.items) if (r.ok) nextSucceeded.add(r.kpiId);
      setSucceededIds(nextSucceeded);

      // Map failures onto per-row error text.
      setRows((prev) => {
        const next: typeof prev = { ...prev };
        for (const r of response.items) {
          if (!r.ok) {
            next[r.kpiId] = { ...next[r.kpiId], error: r.error };
          }
        }
        return next;
      });

      if (failCount === 0) {
        setBanner(null);
        onClose();
      } else {
        setBanner(
          `${okCount} of ${response.items.length} logged. ${failCount} failed — see below.`,
        );
      }
    },
    [
      activeKpis,
      contractId,
      measurements,
      mutation,
      onClose,
      reports,
      rows,
      succeededIds,
    ],
  );

  if (!isOpen) return null;

  const isPending = mutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log this period across KPIs"
      maxWidth="sm:max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {banner && <Alert variant="warning">{banner}</Alert>}

        {activeKpis.length === 0 ? (
          <p className="py-6 text-center text-body-sm text-slate-500 dark:text-slate-400">
            No KPIs to log. Add one in the Definitions view first.
          </p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {activeKpis.map((kpi) => {
              const row = rows[kpi.id];
              if (!row) return null;
              const autoRag = computeAutoRag(row.value, kpi);
              const effectiveRag: RagRating | null = row.manualRag ?? autoRag;
              const isOverride =
                row.manualRag !== null &&
                autoRag !== null &&
                row.manualRag !== autoRag;
              const targetParse = parseTarget(kpi.target);
              // Per-row adaptive input by valueType. Legacy
              // KPIs without `valueType` default to "percentage".
              const valueType: KpiValueType = kpi.valueType ?? "percentage";
              const suffix =
                valueType === "number"
                  ? (kpi.unit ?? "")
                  : (targetParse?.suffix ?? "");
              const amberDisabled = valueType === "boolean";
              const succeeded = succeededIds.has(kpi.id);

              return (
                <div
                  key={kpi.id}
                  role="group"
                  aria-label={`Row for ${kpi.name}`}
                  data-testid={`bulk-row-${kpi.id}`}
                  className={`rounded-lg border p-3 ${
                    succeeded
                      ? "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20"
                      : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="text-body-sm font-semibold text-slate-900 dark:text-slate-100">
                          {kpi.name}
                        </span>
                        <InfoHint text={kpi.definition ?? ""} />
                      </div>
                      <span className="text-caption text-slate-500 dark:text-slate-400">
                        Target <span className="font-mono">{kpi.target}</span>
                      </span>
                    </div>
                    {succeeded && (
                      <span className="inline-flex items-center gap-1 text-caption text-emerald-700 dark:text-emerald-300">
                        <CheckCircle
                          aria-hidden="true"
                          className="h-3.5 w-3.5"
                        />
                        Logged
                      </span>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label
                        htmlFor={`bulk-period-${kpi.id}`}
                        className="label"
                      >
                        Period
                      </label>
                      <input
                        id={`bulk-period-${kpi.id}`}
                        aria-label={`Period for ${kpi.name}`}
                        aria-invalid={!!row.error}
                        className="input"
                        value={row.period}
                        onChange={(e) =>
                          updateRow(kpi.id, { period: e.target.value })
                        }
                        disabled={isPending || succeeded}
                        maxLength={50}
                      />
                    </div>
                    {valueType === "boolean" ? (
                      <fieldset aria-invalid={!!row.error}>
                        <legend className="label">Value</legend>
                        <div
                          className="flex flex-wrap gap-2"
                          data-testid={`bulk-value-boolean-${kpi.id}`}
                        >
                          {(
                            [
                              { value: KPI_BOOLEAN_MET, label: "Met" },
                              { value: KPI_BOOLEAN_NOT_MET, label: "Not met" },
                            ] as const
                          ).map((opt) => (
                            <label
                              key={opt.value}
                              className="inline-flex items-center gap-1 text-caption text-slate-700 dark:text-slate-300"
                            >
                              <input
                                type="radio"
                                name={`bulk-value-${kpi.id}`}
                                className="accent-brand-600"
                                value={opt.value}
                                checked={row.value === opt.value}
                                onChange={() => {
                                  // Clear stale amber override — same guard
                                  // as KpiLogPeriodEntry. Backend rejects
                                  // amber for boolean KPIs at write time.
                                  const patch: Partial<RowState> = {
                                    value: opt.value,
                                  };
                                  if (row.manualRag === "amber") {
                                    patch.manualRag = null;
                                  }
                                  updateRow(kpi.id, patch);
                                }}
                                disabled={isPending || succeeded}
                                aria-label={`${opt.label} for ${kpi.name}`}
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ) : (
                      <div>
                        <label
                          htmlFor={`bulk-value-${kpi.id}`}
                          className="label"
                        >
                          Value
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            id={`bulk-value-${kpi.id}`}
                            aria-label={`Value for ${kpi.name}`}
                            aria-invalid={!!row.error}
                            className="input flex-1"
                            value={row.value}
                            onChange={(e) =>
                              updateRow(kpi.id, { value: e.target.value })
                            }
                            disabled={isPending || succeeded}
                            inputMode="decimal"
                          />
                          {suffix && (
                            <span className="text-caption text-slate-500 dark:text-slate-400">
                              {suffix}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <fieldset aria-invalid={!!row.error}>
                      <legend className="label">RAG</legend>
                      <div className="flex flex-wrap gap-2">
                        {RAG_OPTIONS.map((r) => {
                          const amberGated =
                            amberDisabled && r === "amber";
                          const disabled =
                            isPending || succeeded || amberGated;
                          return (
                            <label
                              key={r}
                              className={`inline-flex items-center gap-1 text-caption ${
                                amberGated
                                  ? "text-slate-400 dark:text-slate-500"
                                  : "text-slate-700 dark:text-slate-300"
                              }`}
                              title={
                                amberGated ? BOOLEAN_AMBER_TOOLTIP : undefined
                              }
                            >
                              <input
                                type="radio"
                                name={`bulk-rag-${kpi.id}`}
                                className="accent-brand-600"
                                value={r}
                                checked={effectiveRag === r}
                                onChange={() =>
                                  updateRow(kpi.id, { manualRag: r })
                                }
                                disabled={disabled}
                                aria-label={
                                  amberGated
                                    ? `Amber (${BOOLEAN_AMBER_TOOLTIP})`
                                    : undefined
                                }
                              />
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </label>
                          );
                        })}
                      </div>
                      {isOverride && autoRag && (
                        <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                          Manually overridden — auto-calculated was{" "}
                          {autoRag.charAt(0).toUpperCase() + autoRag.slice(1)}
                        </p>
                      )}
                    </fieldset>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {!row.commentaryOpen ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() =>
                          updateRow(kpi.id, { commentaryOpen: true })
                        }
                        disabled={isPending || succeeded}
                      >
                        Add commentary
                      </Button>
                    ) : (
                      <div className="w-full">
                        <label
                          htmlFor={`bulk-commentary-${kpi.id}`}
                          className="label"
                        >
                          Commentary
                        </label>
                        <textarea
                          id={`bulk-commentary-${kpi.id}`}
                          className="input min-h-[60px]"
                          value={row.commentary}
                          onChange={(e) =>
                            updateRow(kpi.id, { commentary: e.target.value })
                          }
                          disabled={isPending || succeeded}
                          maxLength={2000}
                        />
                      </div>
                    )}
                    {!row.evidenceOpen ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() =>
                          updateRow(kpi.id, { evidenceOpen: true })
                        }
                        disabled={isPending || succeeded}
                      >
                        Add evidence
                      </Button>
                    ) : (
                      <div className="w-full">
                        <EvidenceLinksField
                          value={row.evidenceLinks}
                          onChange={(links) =>
                            updateRow(kpi.id, { evidenceLinks: links })
                          }
                          disabled={isPending || succeeded}
                        />
                      </div>
                    )}
                  </div>

                  {row.error && (
                    <p
                      className="mt-2 text-caption text-red-600 dark:text-red-400"
                      role="alert"
                    >
                      {row.error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          {activeKpis.length > 0 && (
            <Button type="submit" loading={isPending} disabled={isPending}>
              Save logs
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}
